import classNames from 'classnames';
import { Link, useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import { useScreenMediaQuery } from '../../../../utils/use-screen-media-query';
import NewDropdown from '../../../general-ui/dropdown/new-dropdown';
import { Channel, EPreviewTypes, LanguagesAbbr, NavMainItems } from "../../../../types/working-model";
import { ChannelAvatar } from "../../../admin/header/header";
import { EIcon } from '../../../general-ui/icon/icon';
import Logo from "../../../../images/header-image.png";
import './marketing-nav.scss';
import { TemplateClassNames } from '../../../../types/template-layouts';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { eventPath } from '../../utils';
import { useTranslation } from 'react-i18next';
import { GetDefaultMainNavItems } from '../../../../store/utils/create-event';
import { useState } from 'react';
import { setStorageItem } from '../../../../utils/local-storage';
import { LanguageDropdown } from '../../../general-ui/language-dropdown/language-dropdown';
// import { ProfileDetails } from '../../../general-ui/profile-details/profile-details';
import ProfileDetailsDropdown from './profile-details-dropdown';
// import { Notifications } from '../../modules/notifications/notifications';

function getMainNavIcon(name: string) {
	switch (name.toUpperCase()) {
		case 'HOME':
			return <EIcon name="home" size="giant" />;
		case 'SESSION':
			return <EIcon name="event" size="giant" />;
		case 'SESSIONS':
			return <EIcon name="event" size="giant" />;
	}
}

interface MarketingNavProps {
	currentChannel?: Channel;
	template: TemplateClassNames;
	previewHide?: boolean;
	logo?: string;
	previewLanguage?: LanguagesAbbr
	showLanguageSelector?: boolean;
}

export default function MarketingNav({
	currentChannel,
	template,
	previewHide,
	logo,
	previewLanguage,
	showLanguageSelector = false,
}: MarketingNavProps): JSX.Element {

	if (previewHide) return <></>;
	else {
		return (
			<header
				className={classNames(
					'marketing-page-navigation',
					template
				)}
			>
				{currentChannel ? (
					<ChannelAvatar channel={currentChannel} />
				) : (
					<img className="navigation-logo" src={logo ?? Logo} alt={"Brandlive"} width="48" height="48" />
				)}
				<nav className="navigation">
					<MarketingNavMenu showLanguageSelector={showLanguageSelector} previewLanguage={previewLanguage} />
				</nav>
			</header>
		);
	}
}

// Render nav links and handle functionality,
// abstracted from MarketingNav to be used in session header
interface IMarketingNavMenuProps {
	isEditorDesktop?: boolean;
	previewLanguage?: LanguagesAbbr;
	template?: TemplateClassNames;
	showLanguageSelector?: boolean;
}

export const MarketingNavMenu: React.FC<IMarketingNavMenuProps> = ({
	template,
	isEditorDesktop = true,
	previewLanguage,
	showLanguageSelector,
}) => {
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const profile = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const channel = useTypedSelector(state => state?.LiveEventReducer?.eventBundle?.channel);

	const { isLessThan1024, isLessThan768 } = useScreenMediaQuery();
	const { pathname } = useLocation();
	const { uuid, eventName, language: pathLanguage }: any = useParams();

	// temp comment out until notifications is ready
	// const [notificationsOpen, setNotificationsOpen] = useState(false);
	const [selectedLanguage, setSelectedLanguage] = useState(pathLanguage ?? 'en');

	const language = previewLanguage ? previewLanguage : pathLanguage; //when in preview modal portal we no longer have access to params

	const match = useRouteMatch();
	const { t } = useTranslation('homepage');
	const editorLessThan1024 = (editorSize === 'mobile') || (editorSize === 'tablet');

	const isOnSessionURL = match.url === mainNavItemPathMap().sessions || match.url === mainNavItemPathMap().session;

	function mainNavItemPathMap(): { [key: string]: string } {
		return {
			home: `/${eventPath(eventName, uuid)}/${language}/home`,
			sessions: `/${eventPath(eventName, uuid)}/${language}/sessions`,
			// this tab only appears when there is a single session
			session: `/${eventPath(eventName, uuid)}/${language}/session/${eventBundle?.sessions[0]?.uuid}`
		};
	}

	const renderEventMainNavItems = eventBundle?.homepage?.event_main_nav_items?.map(mainNavItem => {
		// check if in a session and tab is sessions
		const isSessionsView = isOnSessionURL && mainNavItem.name.toLowerCase() === 'sessions';

		let name: any;
		let useTranslate = false;
		if (!mainNavItem.custom_name[language] && ["Home", "Agenda", "Sessions"].includes(mainNavItem.custom_name.base)) {
			const base = mainNavItem.custom_name.base as "Home" | "Agenda" | "Sessions";
			name = t(base);
			useTranslate = true;
		}

		return (
			<Link
				key={mainNavItem.uuid}
				className={classNames('navigation-link', { active: pathname.includes(mainNavItem.name.toLowerCase()) || isSessionsView })}
				to={mainNavItemPathMap()[mainNavItem.name.toLowerCase()] || pathname}
			>
				{!isLessThan1024 && getMainNavIcon(mainNavItem.name)}
				{useTranslate ? name : (mainNavItem.custom_name[language] ?? mainNavItem.custom_name.base)}
			</Link>
		);
	});

	// get the nav buttons from the working event if the current event is rendering in the preview modal
	const isSingleSession = workingEvent?.sessions.length === 1;
	const eventMainNavItems = workingEvent?.homepage?.event_main_nav_items || GetDefaultMainNavItems(isSingleSession);
	const renderEventPreviewNavItems = eventMainNavItems.map(mainNavItem => {
		const isHomeActive = (previewMode === EPreviewTypes.Home && mainNavItem.name === NavMainItems.Home);
		const isAgendaActive = (previewMode === EPreviewTypes.Agenda && mainNavItem.name === NavMainItems.Sessions);

		let name: any;
		let useTranslate = false;
		if (!mainNavItem.custom_name[language] && ["Home", "Agenda", "Sessions"].includes(mainNavItem.custom_name.base)) {
			const base = mainNavItem.custom_name.base as "Home" | "Agenda" | "Sessions";
			name = t(base);
			useTranslate = true;
		}

		return (
			<Link
				key={mainNavItem.uuid}
				className={classNames('navigation-link', (isHomeActive || isAgendaActive) ? 'active' : '')}
				// to={mainNavItemPathMap()[mainNavItem.name.toLowerCase()] || pathname}
				to={'#'}
			>
				{!isLessThan1024 && getMainNavIcon(mainNavItem.name)}
				{useTranslate ? name : (mainNavItem.custom_name[language] ?? mainNavItem.custom_name.base)}
			</Link>
		);
	});

	const languages = Array.from(new Set(eventBundle?.homepage?.languages as LanguagesAbbr[]));

	const history = useHistory();
	const routeMatch = useRouteMatch<any>();
	const handleLanguageChange = (langAbbr: LanguagesAbbr) => {
		if (!eventBundle?.event) return;
		if (!routeMatch?.params?.language) return;
		setSelectedLanguage(langAbbr);
		// language is set in storage so we can remember the users chosen language so if they enter
		// a session in another language and then go back to the home page, we can reset the language to their chosen language
		setStorageItem(`event.${eventBundle.event}.language`, langAbbr);
		if (langAbbr && langAbbr !== routeMatch?.params?.language) {
			// replace the language in the url with langAbbr
			const updatedParams = { ...routeMatch.params };
			updatedParams.language = langAbbr;
			let updatedUrl = routeMatch.path;
			for (const key in updatedParams) {
				updatedUrl = updatedUrl.replace(`:${key}`, updatedParams[key]);
			}
			history.replace(updatedUrl);
		}
	};

	return (
		<>
			{(isLessThan1024 || !isEditorDesktop || editorLessThan1024) ? (
				<div className="moble-header-actions-container">
					{showLanguageSelector ? (
						<div className={`mobile-header-language-selector ${!isLessThan768 ? 'left-align' : ''}`}>
							<LanguageDropdown
								template={template}
								languages={languages}
								currentLanguageCode={selectedLanguage}
								onLanguageChange={handleLanguageChange}
							/>
						</div>
					) : null}
					<NewDropdown
						className={classNames('menu-tablet', { 'has-language-selector': showLanguageSelector })}
						popupClassName="menu-tablet-popup"
						showArrow={false}
						renderTitle={({ isOpen }) =>
							isOpen ? (
								<EIcon name="close" size="large" />
							) : (
								<EIcon name="menu" size="large" />
							)
						}
					>
						<div className="tablet-links">{previewMode === EPreviewTypes.Session ? renderEventPreviewNavItems : renderEventMainNavItems}</div>
					</NewDropdown>
				</div>
			) : previewMode ? renderEventPreviewNavItems : renderEventMainNavItems}

			{/* TEMP: use !isLessThan1024 for now. Not showing the profile dropdown on smaller screens because there are no */}
			{/* designs for it and it's only got the language selector, which is already in the header on mobile */}
			{!isLessThan1024 && eventBundle && channel ? (
				<aside className="profile-details-actions">
					{/* Comment out notifications icon until ready */}
					{/* <button
						className={classNames(
							'notification-preview-trigger no-style',
							{ open: notificationsOpen },
						)}
						aria-label={
							notificationsOpen
								? 'Close notification pop-up'
								: 'Open notification pop-up'
						}
						onClick={() => setNotificationsOpen(!notificationsOpen)}
					>
						<EIcon name="reminder-on" size={24} />
						// TODO: Add unread message counter
						<span className="notification-preview-trigger-badge">
							12
						</span>
					</button> */}

					{/* Comment out notifications until ready */}
					{/* <Notifications
						open={notificationsOpen}
						onClose={() => setNotificationsOpen(false)}
						style={{ background: '#fff' }}
						template={template}
					/> */}

					<ProfileDetailsDropdown
						profile={profile}
						channel={channel}
						eventBundle={eventBundle}
						template={template || TemplateClassNames.Apollo}
					/>
				</aside>
			) : null}
		</>
	);
};
